// Small custom hooks that don't justify their own file.

import { useEffect, useRef, useState } from 'react';
import { subscribeToasts, type Toast } from './toast';

export function useToasts(): Toast[] {
  const [toasts, setToasts] = useState<Toast[]>([]);
  useEffect(() => subscribeToasts(setToasts), []);
  return toasts;
}

/** Global keyboard shortcut handler. `bindings` is a map of `key -> handler`. Modifier-only
 *  matches (no shift/ctrl/meta/alt unless the key itself implies one); ignores keys typed in
 *  inputs/textareas unless the binding's value is `{ allowInInput: true, ... }`. */
export interface ShortcutBinding {
  handler: (e: KeyboardEvent) => void;
  allowInInput?: boolean;
}

export function useShortcuts(bindings: Record<string, ShortcutBinding | ((e: KeyboardEvent) => void)>) {
  // Callers pass a fresh object literal each render. Stash it in a ref (updated every render)
  // so the keydown listener stays current without tearing down + re-attaching on every render.
  const bindingsRef = useRef(bindings);
  bindingsRef.current = bindings;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const norm: Record<string, ShortcutBinding> = {};
      for (const [k, v] of Object.entries(bindingsRef.current)) {
        norm[k] = typeof v === 'function' ? { handler: v } : v;
      }
      // Don't fire while typing in a form field (unless the binding opts in).
      const target = e.target as HTMLElement | null;
      const inEditable =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable === true;
      const key = e.key;
      const b = norm[key];
      if (!b) return;
      if (inEditable && !b.allowInInput) return;
      // Ignore modifier-augmented presses (so cmd-K stays unbound by default).
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      b.handler(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
